import {
  IComment,
  IPost,
  fetchComments,
  fetchCommentsCount,
} from "@/lib/community";
import { formatDate } from "@/utils/formatDate";
import Comment from "./Comment";
import CommentForm from "./CommentForm";
import CustomAvatar from "./CustomAvatar";
import LikeButton from "./LikeButton";
import MediaSlider from "./MediaSlider";
import { DynamicTextWithLink } from "./DynamicTextWithLink";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { MixpanelActions } from "@/integrations/mixpanel/mixpanel";
import { ArrowDown, Pencil, Trash2 } from "lucide-react";
import { PostEditForm } from "./PostEditForm";
import { useUser } from "@supabase/auth-helpers-react";

interface Props {
  post: IPost;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  onUpdate?: (updated: IPost) => void;
  onDelete?: (id: string) => void;
  commentId?: string;
}

export default function PostContent({
  post,
  scrollContainerRef,
  onUpdate,
  onDelete,
  commentId,
}: Props) {
  const { t } = useTranslation();
  const user = useUser();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showJumpButton, setShowJumpButton] = useState(false);
  const commentFormRef = useRef<HTMLDivElement>(null);
  const commentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const editable = useMemo(() => user.id === post.author, [user, post]);
  const media = useMemo(
    () => (post.media ? post.media.map((e) => JSON.parse(e)) : []),
    [post.media]
  );

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ["comments", post.id],
    queryFn: () => fetchComments(post.id, null),
    enabled: !!post.id,
  });

  const handleAddComment = (comment: IComment) => {
    queryClient.setQueryData<IComment[]>(["comments", post.id], (old = []) => [
      ...old,
      comment,
    ]);
  };

  const userName = useMemo(() => post.authorData?.username || "", [post]);

  const handleJumpToLast = () => {
    commentFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  useEffect(() => {
    const el = commentFormRef.current;
    const container = scrollContainerRef?.current ?? window;
    if (!el || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowJumpButton(!entry.isIntersecting),
      {
        root: container instanceof HTMLElement ? container : null,
        threshold: 0.1,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollContainerRef, comments.length]);

  useEffect(() => {
    if (commentId && commentRefs.current[commentId]) {
      commentRefs.current[commentId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [commentId, comments.length]);

  return (
    <>
      <div className="flex justify-between">
        <div className="flex items-start gap-3">
          <Link
            to={`/user/${post.author}`}
            onClick={() => MixpanelActions.clickUserProfile()}
          >
            <CustomAvatar userData={post.authorData} />
          </Link>
          <div className="text-sm">
            <Link
              to={`/user/${post.author}`}
              onClick={() => MixpanelActions.clickUserProfile()}
            >
              <div className="font-semibold">{userName}</div>
            </Link>
            <div className="text-gray-500 text-xs">
              {formatDate(post.created_at, t)}
            </div>
          </div>
        </div>
      </div>

      {!isEditing ? (
        <div className="space-y-4">
          <div className="relative">
            <div className="pr-4">
              <h2 className="font-bold text-lg mb-2">{post.title}</h2>
              <DynamicTextWithLink
                text={post.content}
                className="text-gray-800 whitespace-pre-wrap"
              />
            </div>
            {editable && (
              <>
                <Pencil
                  className="absolute top-0 right-8 cursor-pointer hover:text-primary"
                  size={16}
                  onClick={() => setIsEditing(true)}
                />
                <Trash2
                  className="absolute top-0 right-0 cursor-pointer hover:text-red-600"
                  size={16}
                  onClick={() => onDelete?.(post.id)}
                />
              </>
            )}
          </div>
          {media.length > 0 && (
            <div className="mt-4">
              <MediaSlider media={media} />
            </div>
          )}

          <div className="flex items-center gap-6 text-sm text-gray-500 border-y py-2">
            <LikeButton parentId={post.id} parentType="post" />
            <div>
              {t("community.postModal.commentsCount", {
                count: post.comments.at(0).count,
              })}
            </div>
          </div>
        </div>
      ) : (
        <PostEditForm
          post={post}
          onSave={(updated) => {
            setIsEditing(false);
            onUpdate?.(updated);
          }}
          onCancel={() => setIsEditing(false)}
        />
      )}

      <div className="space-y-2">
        {isLoading ? (
          <div>{t("community.postModal.loadingComments")}</div>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              ref={(el) => (commentRefs.current[comment.id] = el)}
            >
              <Comment comment={comment} postId={post.id} depth={0} />
            </div>
          ))
        )}
      </div>

      <CommentForm
        ref={commentFormRef}
        postId={post.id}
        onCommentAdd={handleAddComment}
      />

      {showJumpButton && (
        <div className="sticky bottom-20 w-full flex justify-center z-40">
          <button
            type="button"
            onClick={handleJumpToLast}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-2xl shadow-xl hover:bg-primary/80 transition-all duration-150 font-medium backdrop-blur-md backdrop-filter"
            style={{ boxShadow: "0 4px 32px 0 rgba(60, 60, 60, 0.07)" }}
          >
            <ArrowDown size={20} />
            <span className="text-base">
              {t("community.postModal.jumpToLatest")}
            </span>
          </button>
        </div>
      )}
    </>
  );
}
